import { useState } from "react";
import { RefreshCw, X } from "lucide-react";

/**
 * Bottom-corner, non-blocking "تحديث جديد متاح" notice.
 *
 * Shown when the app detects a stale chunk/version mismatch after a deploy.
 * Never auto-reloads. Never blocks the current screen. The user keeps full
 * access to whatever page they were on; they choose when to refresh.
 *
 * Reload is cache-busting (?_v=ts) to bypass any CDN-cached HTML still
 * pointing at old chunk hashes.
 */
export default function UpdateAvailableBanner({ onDismiss }) {
  const [reloading, setReloading] = useState(false);

  const handleReload = () => {
    setReloading(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_v", String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      dir="rtl"
      className="fixed bottom-4 end-4 z-[100] max-w-[22rem] w-[calc(100vw-2rem)] sm:w-auto pointer-events-none"
    >
      <div className="pointer-events-auto bg-card/95 backdrop-blur-md border border-border shadow-xl rounded-xl p-3 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary shrink-0">
          <RefreshCw className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-tight">
            تحديث جديد متاح
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            يتوفر إصدار أحدث من النظام. حدّث الصفحة عندما يناسبك.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleReload}
              disabled={reloading}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-3 py-1.5 disabled:opacity-60 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${reloading ? "animate-spin" : ""}`} />
              {reloading ? "جاري التحديث…" : "تحديث الآن"}
            </button>
          </div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="إغلاق"
            className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
